<template>
  <v-container class="login-container">
    <!-- One button per configured SSO provider (from VUE_APP_SSO_PROVIDERS) -->
    <v-form v-if="providers.length">
      <v-btn
        v-for="p in providers"
        :key="p.name"
        class="login-button mb-2"
        block
        @click="loginWithProvider(p.name)"
      >
        {{ p.label || ("Sign in with " + p.name) }}
      </v-btn>
    </v-form>

    <!-- Discreet local-admin path (tester) -->
    <div class="admin-toggle">
      <a href="#" @click.prevent="showAdmin = !showAdmin">Admin login</a>
    </div>

    <v-form v-if="showAdmin" class="admin-form" @submit.prevent="loginLocalAdmin">
      <v-text-field
        v-model="username"
        label="Email"
        type="email"
        autocomplete="username"
        density="compact"
      />
      <v-text-field
        v-model="password"
        label="Password"
        type="password"
        autocomplete="current-password"
        density="compact"
      />

      <!-- Shown only when Cognito requires a new password on first login -->
      <v-text-field
        v-if="newPasswordRequired"
        v-model="newPassword"
        label="New password"
        type="password"
        autocomplete="new-password"
        density="compact"
      />

      <v-btn type="submit" class="login-button" :loading="loading">
        {{ newPasswordRequired ? "Set password" : "Sign in" }}
      </v-btn>

      <p v-if="error" class="admin-error">{{ error }}</p>
    </v-form>
  </v-container>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { isLoggedIn, isTokenExpired } from "@/utils/auth";
import { useRouter } from "vue-router";
import { Amplify, Auth } from "aws-amplify";
import { ssoProviders, awsRegion } from "@/config/sso";

const router = useRouter();

const providers = ssoProviders();
const showAdmin = ref(false);
const loading = ref(false);
const error = ref("");
const username = ref("");
const password = ref("");
const newPassword = ref("");
const newPasswordRequired = ref(false);
let pendingUser = null;

onMounted(() => {
  Amplify.configure({
    Auth: {
      region: awsRegion,
      userPoolId: process.env.VUE_APP_COGNITO_USER_POOL_ID,
      userPoolWebClientId: process.env.VUE_APP_COGNITO_CLIENT_ID,
      mandatorySignIn: false,
      oauth: {
        domain: process.env.VUE_APP_COGNITO_DOMAIN,
        scope: ["openid", "email"],
        redirectSignIn: process.env.VUE_APP_SIGN_IN_REDIRECT,
        redirectSignOut: process.env.VUE_APP_SIGN_IN_REDIRECT,
        responseType: "token",
      },
    },
  });

  if (isLoggedIn() && !isTokenExpired()) {
    router.push({ name: "Clients" });
  }
});

// Federated sign-in via the Cognito hosted UI for any configured provider.
function loginWithProvider(providerName) {
  Auth.federatedSignIn({ provider: providerName });
}

// Store tokens the same way the hosted-UI flow does, so the router guard and
// API layer read them identically.
function storeSessionAndRedirect(cognitoUser) {
  const session = cognitoUser.getSignInUserSession();
  const accessToken = session.getAccessToken().getJwtToken();
  const idToken = session.getIdToken().getJwtToken();
  localStorage.setItem("accessToken", accessToken);
  localStorage.setItem("idToken", idToken);
  router.push({ name: "Clients" });
}

async function loginLocalAdmin() {
  error.value = "";
  loading.value = true;
  try {
    if (newPasswordRequired.value) {
      const user = await Auth.completeNewPassword(pendingUser, newPassword.value);
      storeSessionAndRedirect(user);
      return;
    }

    const user = await Auth.signIn(username.value, password.value);

    // First-login flow: Cognito requires the temporary password to be changed.
    if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
      pendingUser = user;
      newPasswordRequired.value = true;
      error.value = "Please set a new password.";
      return;
    }

    storeSessionAndRedirect(user);
  } catch (e) {
    error.value = e && e.message ? e.message : "Sign-in failed.";
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped>
.login-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.admin-toggle {
  margin-top: 8px;
  font-size: 0.85rem;
  opacity: 0.7;
}
.admin-form {
  width: 320px;
  max-width: 90vw;
}
.admin-error {
  color: #c0392b;
  font-size: 0.85rem;
  margin-top: 8px;
}
</style>
